import React from 'react';
import axios from 'axios';
import { all, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { get, isEmpty, sortBy } from 'lodash';
import {
  downloadAllAnswerSheets,
  downloadIndividualAnswerSheet,
  generateAnswerSheets,
  generateAnswerSheetsPreview,
  downloadGeneratedAnswerSheet as downloadGeneratedAnswerSheetAction,
  initiateDownloadForUser as initiateDownloadForUserAction,
} from 'legacy/src/actions/paperscorer';
import { toast as toastify } from 'chameleon/ui-stack/ui/toast';
import { toast } from 'legacy-packages/ft-cb';
import DownloadNowLink from 'legacy/src/components/paperscorer/DownloadNowLink';
import log from 'chameleon/ui-stack/utilities/log';

export function* generatePaperscorerAnswerSheet({
  payload: {
    assignmentPaperscorerAnswerSheet,
    frqPrintSettings,
    assignmentName,
    sectionName,
    subjectName,
    assignmentProgressLink,
    ...formValues
  },
}) {
  try {
    const assignmentJsonData = get(
      JSON.parse(assignmentPaperscorerAnswerSheet),
      'json_data',
      {},
    );
    const students = get(formValues, 'students', []);
    const questions = !isEmpty(frqPrintSettings)
      ? [
          ...assignmentJsonData.questions.filter((config) => config.type === 2),
          ...Object.values(frqPrintSettings).reduce(
            (acc, curr) => [...acc, ...Object.values(curr)],
            [],
          ),
        ]
      : assignmentJsonData.questions;

    const jsonData = {
      assignment_json_data: {
        ...assignmentJsonData,
        students,
        questions: sortBy(questions, (config) => parseInt(config.text, 10)),
      },
      is_preview: false,
      config_json: '{}',
    };
    const body = JSON.stringify(jsonData);

    const response = yield axios({
      method: 'post',
      url: `${FYM_BACKEND_PAPERSCORER_API_URL}/generate_paperscorer_answer_sheet`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    });
    const {
      status,
      message,
      answer_sheet_url,
      download_url: downloadUrl,
    } = get(response, 'data', {});
    yield put(
      generateAnswerSheets.success({ answerSheetUrl: answer_sheet_url }),
    );
    const payload = {
      status,
      assignmentName,
      sectionName,
      subjectName,
      formUrl: downloadUrl,
    };
    if (status === 1) {
      toastify(
        <span>
          The answer sheets for {assignmentName} are ready. <br />
          <DownloadNowLink payload={payload} /> or
          <a className="text-blue-500 font-bold" href={assignmentProgressLink}>
            {' '}
            reload your assignment page.
          </a>
        </span>,
        { timeout: 20 },
      );
    } else {
      toast.error(
        `Failed to generate the answer sheet, ${message}, please contact support.`,
      );
    }
  } catch (error) {
    toast.error(
      `Failed to generate the answer sheet, request failed, please contact support.`,
    );
    yield put(generateAnswerSheets.failure(error));
    log('error', error);
  }
}

export function* generatePaperscorerAnswerSheetPreview({
  payload: { assignmentPaperscorerAnswerSheet, frqPrintSettings },
}) {
  try {
    const assignmentJsonData = get(
      JSON.parse(assignmentPaperscorerAnswerSheet),
      'json_data',
      {},
    );
    const questions = !isEmpty(frqPrintSettings)
      ? [
          ...assignmentJsonData.questions.filter((config) => config.type === 2),
          ...Object.values(frqPrintSettings).reduce(
            (acc, curr) => [...acc, ...Object.values(curr)],
            [],
          ),
        ]
      : assignmentJsonData.questions;

    const jsonData = {
      assignment_json_data: {
        ...assignmentJsonData,
        students: [
          {
            name: 'Sample Student',
            id: '123456',
            identifier: 'AP Classroom ID 123456',
          },
        ],
        questions: sortBy(questions, (config) => parseInt(config.text, 10)),
      },
      is_preview: true,
      config_json: '{}',
    };
    const body = JSON.stringify(jsonData);

    const response = yield axios({
      method: 'post',
      url: `${FYM_BACKEND_PAPERSCORER_API_URL}/generate_paperscorer_answer_sheet`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    });
    const { status, message, answer_sheet_url } = get(response, 'data', {});
    if (status === 1) {
      yield put(generateAnswerSheetsPreview.success(answer_sheet_url));
    } else {
      toast.error(
        `Failed to preview answer sheet, ${message}, please contact support.`,
      );
    }
  } catch (error) {
    toast.error(
      `Failed to preview answer sheet, request failed, please contact support.`,
    );
    yield put(generateAnswerSheetsPreview.failure(error));
  }
}

export function* downloadAllPaperscorerAnswerSheets({ payload }) {
  const assignmentId = get(payload, 'assignmentId', 0);
  const assignmentName = get(payload, 'assignmentName', '');
  const subjectName = get(payload, 'subjectName', '');
  const sectionName = get(payload, 'sectionName', '');
  try {
    const response = yield axios({
      method: 'get',
      url: `${FYM_BACKEND_PAPERSCORER_API_URL}/get_paperscorer_forms_by_assignment_id`,
      params: { assignment_id: assignmentId },
    });
    const paperscorerForms = get(response, 'data', {});
    if (paperscorerForms.length < 1) {
      yield put(downloadAllAnswerSheets.failure());
      toast.error('No answer sheets were found');
    } else {
      // eslint-disable-next-line no-unused-vars
      for (let formUrl of paperscorerForms) {
        yield put(
          downloadAllAnswerSheets.success({
            formUrl,
            assignmentName,
            subjectName,
            sectionName,
          }),
        );
      }
    }
  } catch (error) {
    toast.error(`Failed to download answer sheets, please contact support.`);
    yield put(downloadAllAnswerSheets.failure(error));
    log('error', error);
  }
}

export function* downloadIndividualPaperscorerAnswerSheet({ payload }) {
  const userId = get(payload, 'userId', 0);
  const assignmentId = get(payload, 'assignmentId', 0);
  const assignmentName = get(payload, 'assignmentName', '');
  const subjectName = get(payload, 'subjectName', '');
  const sectionName = get(payload, 'sectionName', '');
  try {
    const response = yield axios({
      method: 'get',
      url: `${FYM_BACKEND_PAPERSCORER_API_URL}/get_individual_paperscorer_form_by_assignment_id`,
      params: { assignment_id: assignmentId, user_id: userId },
    });
    const paperscorerForm = get(response, 'data', {});
    yield put(
      downloadIndividualAnswerSheet.success({
        formUrl: paperscorerForm,
        assignmentName,
        subjectName,
        sectionName,
      }),
    );
    if (paperscorerForm.length < 1) {
      yield put(downloadIndividualAnswerSheet.failure());
      toast.error('No answer sheet was found');
    }
  } catch (error) {
    toast.error(`Failed to download answer sheets, please contact support.`);
    yield put(downloadIndividualAnswerSheet.failure(error));
    log('error', error);
  }
}

// split up download from service-ps-api from initiating download on client side so
// in case we need to debug, the point of failure would be more obvious
export function* initiateDownloadForUser({ payload }) {
  const formUrl = get(payload, 'formUrl', '');
  const subjectName = get(payload, 'subjectName', '');
  const sectionName = get(payload, 'sectionName', '');
  const assignmentName = get(payload, 'assignmentName', '');
  try {
    const response = yield fetch(formUrl);
    if (response.status === 200) {
      const blob = new Blob([yield response.blob()], {
        type: 'application/pdf',
      });
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `Answer Sheets for ${subjectName}_${assignmentName}_${sectionName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      yield put(initiateDownloadForUserAction.success());
    } else {
      yield put(initiateDownloadForUserAction.failure());
    }
  } catch (error) {
    toast.error(
      'Failed to initiate download for answer sheet, please contact support.',
    );
    yield put(initiateDownloadForUserAction.failure(error));
    log('error', error);
  }
}

export default function* paperscorerSaga() {
  yield all([
    takeLatest(generateAnswerSheets, generatePaperscorerAnswerSheet),
    takeLatest(
      generateAnswerSheetsPreview,
      generatePaperscorerAnswerSheetPreview,
    ),
    takeLatest(downloadAllAnswerSheets, downloadAllPaperscorerAnswerSheets),
    takeLatest(
      downloadIndividualAnswerSheet,
      downloadIndividualPaperscorerAnswerSheet,
    ),
    takeEvery(downloadGeneratedAnswerSheetAction, initiateDownloadForUser),
    takeEvery(downloadAllAnswerSheets.SUCCESS, initiateDownloadForUser),
    takeEvery(downloadIndividualAnswerSheet.SUCCESS, initiateDownloadForUser),
    takeEvery(
      downloadGeneratedAnswerSheetAction.SUCCESS,
      initiateDownloadForUser,
    ),
  ]);
}
